import React from 'react';
import type { IPerformanceComparatorProps } from '../types';

// Normaliza status para evitar quebras por acentos/variações
const normalize = (s: string) =>
  (s || '')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

const stylesByKey = {
  'ativo': {
    border: 'border-green-500',
    bg: 'bg-green-500/10 dark:bg-green-500/20',
    text: 'text-green-700 dark:text-green-300',
    badgeBorder: 'border-green-500/30',
  },
  'em negociacao': {
    border: 'border-yellow-500',
    bg: 'bg-yellow-500/10 dark:bg-yellow-500/20',
    text: 'text-yellow-700 dark:text-yellow-300',
    badgeBorder: 'border-yellow-500/30',
  },
  'finalizado': {
    border: 'border-zinc-500',
    bg: 'bg-zinc-500/10 dark:bg-zinc-500/20',
    text: 'text-zinc-600 dark:text-zinc-400',
    badgeBorder: 'border-zinc-500/30',
  },
} as const;

const defaultStyles = {
  border: 'border-zinc-400',
  bg: 'bg-zinc-500/10 dark:bg-zinc-500/20',
  text: 'text-zinc-600 dark:text-zinc-400',
  badgeBorder: 'border-zinc-500/30',
};

const pickStyles = (status: string) => {
  const n = normalize(status);
  if (n.includes('negoc')) return stylesByKey['em negociacao'];
  if (n.includes('ativo')) return stylesByKey['ativo'];
  if (n.includes('final')) return stylesByKey['finalizado'];
  return defaultStyles;
};

export const PerformanceComparator = ({ partnerships }: IPerformanceComparatorProps) => (
  <div className="bg-surface p-4 rounded-lg card fade-in border border-default" style={{ animationDelay: '0.5s' }}>
    <h3 className="text-base xl:text-lg font-bold text-zinc-900 dark:text-white mb-3">Relacionamento e Negociação com Marcas</h3>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch">
      {partnerships.map((item, index) => {
        const styles = pickStyles(item.status);
        const mainValueDetail = item.details.find(d => d.label.toLowerCase().includes('valor'));
        const otherDetails = item.details.filter(d => !d.label.toLowerCase().includes('valor'));

        return (
          <div key={index} className={`h-full flex flex-col bg-subtle rounded-lg border-l-4 ${styles.border} shadow-lg transition-all duration-300 hover:shadow-green-500/10`}>
            <div className="p-3">
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-4">
                  <img src={item.brandLogo} alt={`${item.brandName} logo`} className="w-12 h-12 rounded-full bg-white object-cover"/>
                  <div>
                    <p className="font-bold text-zinc-900 dark:text-white text-lg">{item.brandName}</p>
                    <p className="text-sm text-zinc-500 dark:text-zinc-400">Artista: {item.artistName}</p>
                  </div>
                </div>
                <span className={`text-xs font-bold px-3 py-1 rounded-full border ${styles.badgeBorder} ${styles.bg} ${styles.text} whitespace-nowrap`}>
                  {item.status}
                </span>
              </div>

              {mainValueDetail && (
                <div className="my-3 border-t border-b border-zinc-200 dark:border-zinc-700/50 py-3">
                  <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-1">{mainValueDetail.label}</p>
                  <p className="text-2xl font-bold text-zinc-900 dark:text-white flex items-center gap-2">
                    <i className={`${mainValueDetail.icon} text-2xl text-zinc-400 dark:text-zinc-500`}></i>
                    {mainValueDetail.value}
                  </p>
                </div>
              )}
            </div>

            {otherDetails.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3 p-3 bg-zinc-100 dark:bg-black/20 rounded-b-md mt-auto">
                {otherDetails.map(detail => (
                  <div key={detail.label}>
                    <p className="flex items-center gap-2 text-sm text-zinc-500 dark:text-zinc-400">
                      <i className={`${detail.icon} w-4 text-center`}></i>
                      <span>{detail.label}</span>
                    </p>
                    <p className="font-semibold text-zinc-900 dark:text-white mt-1 pl-6 text-sm">{detail.value}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  </div>
);

